'use client'
import { useState, useEffect } from 'react'

const REGIONS = [
  { key: 'all',         label: 'All' },
  { key: 'europe',      label: 'Europe' },
  { key: 'asia',        label: 'Asia' },
  { key: 'americas',    label: 'Americas' },
  { key: 'middle_east', label: 'Middle East' },
  { key: 'africa',      label: 'Africa' },
  { key: 'oceania',     label: 'Oceania' },
]

const SORTS = [
  { key: 'score',    label: 'Best match' },
  { key: 'price',    label: 'Lowest price' },
  { key: 'discount', label: 'Most below avg' },
  { key: 'newest',   label: 'Recently detected' },
]

export default function DealsSearchSortBar({ searchText, setSearchText, sortBy, setSortBy, regionFilter, setRegionFilter, regionCounts }) {
  const [inputValue, setInputValue] = useState(searchText)

  // Keep local input in sync if parent clears it (e.g. "clear all filters")
  useEffect(() => {
    if (searchText === '') setInputValue('')
  }, [searchText])

  // Debounce: propagate to parent 150ms after last keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearchText(inputValue), 150)
    return () => clearTimeout(t)
  }, [inputValue]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-5 flex flex-col gap-3">
      {/* Region pills */}
      <div className="flex flex-wrap gap-1.5">
        {REGIONS.map(({ key, label }) => {
          const count = regionCounts?.[key]
          return (
            <button
              key={key}
              onClick={() => setRegionFilter(key)}
              className={[
                'px-3 py-1 rounded-full text-sm font-medium transition-colors',
                regionFilter === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50',
              ].join(' ')}
            >
              {label}{count != null ? <span className={`ml-1 text-xs font-normal tabular-nums ${regionFilter === key ? 'text-blue-200' : 'text-gray-500'}`}>({count})</span> : null}
            </button>
          )
        })}
      </div>

      {/* Search + sort */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative min-w-[180px] flex-1 max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Search destination…"
            className="w-full pl-8 pr-7 py-1.5 rounded-full border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {inputValue && (
            <button
              onClick={() => { setInputValue(''); setSearchText('') }}
              aria-label="Clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs"
            >✕</button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {SORTS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSortBy(key)}
              className={[
                'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                sortBy === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
